import { useMemo, useRef, useState, useEffect } from "react";
import {
  PoundSterling,
  AlertTriangle,
  CreditCard,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { formatCreditLimit } from "@/lib/utils";
import { useCustomerMetricTotals } from "@/hooks/useTransactionsGraphQL";
import type { CurrencyMode } from "@/components/shared/currency-toggle";
import { ScopeLabel } from "@/components/customers/scope-label";

// Currency symbols lookup — used for display only.
// Both customer-currency and base-currency values are supplied directly
// from the Finance system; no FX arithmetic is performed here.
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  CAD: "CA$",
  AUD: "A$",
};

// Types
interface Customer {
  id: string;
  name: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  currencyCreditLimit?: number;
  dso: number;
  lastPayment: string;
  paymentTerms: string;
  otherCompaniesCount?: number;
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  invoiceDate?: string;
}

interface CustomerMetricCardsProps {
  customer: Customer;
  payments: Payment[];
  onCardClick: (action: "outstanding" | "overdue" | "credit" | "performance") => void;
  isConsolidatedView?: boolean;
  isMultiCompanyGroup?: boolean;
  detailCurrencyMode?: CurrencyMode;
  /** For Any Currency accounts: the currently selected currency in the header dropdown.
   *  When set to the base currency (or undefined) all transactions are included.
   *  When set to a specific currency, only transactions in that currency are included. */
  selectedCurrency?: string;
  /** The company's base currency code — used to determine when to show all transactions. */
  baseCurrency?: string;
}

// Helper function to format currency with 2 decimal places
const formatCurrencyDetailed = (value: number, currency: string): string => {
  const symbol = CURRENCY_SYMBOLS[currency] ?? "£";
  return `${symbol}${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Helper function to format date
const formatDateShort = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export function CustomerMetricCards({
  customer,
  payments,
  onCardClick,
  isConsolidatedView = false,
  isMultiCompanyGroup = false,
  detailCurrencyMode = "customer",
  selectedCurrency,
  baseCurrency,
}: Readonly<CustomerMetricCardsProps>) {
  // Fade state for smooth value transition when currency mode or any-currency selection changes
  const [isVisible, setIsVisible] = useState(true);
  const prevModeRef = useRef(detailCurrencyMode);
  const prevCurrencyRef = useRef(selectedCurrency);
  useEffect(() => {
    const modeChanged = prevModeRef.current !== detailCurrencyMode;
    const currencyChanged = prevCurrencyRef.current !== selectedCurrency;
    if (!modeChanged && !currencyChanged) return;
    prevModeRef.current = detailCurrencyMode;
    prevCurrencyRef.current = selectedCurrency;
    setIsVisible(false);
    const t = setTimeout(() => setIsVisible(true), 150);
    return () => clearTimeout(t);
  }, [detailCurrencyMode, selectedCurrency]);

  // Resolve the effective display currency for this customer/view combination.
  const effectiveCurrency = useMemo(() => {
    const base = baseCurrency ?? "GBP";
    if (selectedCurrency) return selectedCurrency;
    if (detailCurrencyMode === "base") return base;
    return customer.currency;
  }, [selectedCurrency, detailCurrencyMode, baseCurrency, customer.currency]);

  const isNonBaseCurrencyActive = effectiveCurrency !== (baseCurrency ?? "GBP");

  // Toggle-aware totals: respects both currency mode and consolidated view toggle.
  const { data: metricTotals } = useCustomerMetricTotals(
    customer.id,
    isNonBaseCurrencyActive ? effectiveCurrency : undefined,
    isConsolidatedView,
  );

  // Credit Available always uses group-level outstanding (credit limits are per group).
  const { data: groupMetricTotals } = useCustomerMetricTotals(
    customer.id,
    isNonBaseCurrencyActive ? effectiveCurrency : undefined,
    true,
  );

  const metrics = useMemo(() => {
    const outstandingBalance = isNonBaseCurrencyActive
      ? metricTotals.currencyOutstanding
      : metricTotals.baseOutstanding;
    const outstandingCount = metricTotals.outstandingCount;

    const overdueBalance = isNonBaseCurrencyActive
      ? metricTotals.currencyOverdue
      : metricTotals.baseOverdue;
    const overdueCount = metricTotals.overdueCount;

    const effectiveCreditLimit = isNonBaseCurrencyActive
      ? (customer.currencyCreditLimit ?? customer.creditLimit)
      : customer.creditLimit;

    // Credit Available is always group-level (credit limits are per group).
    const groupOutstanding = isNonBaseCurrencyActive
      ? groupMetricTotals.currencyOutstanding
      : groupMetricTotals.baseOutstanding;
    const availableCredit = effectiveCreditLimit - groupOutstanding;

    // Credit Usage % respects the consolidated toggle.
    const creditUsedPercentage =
      effectiveCreditLimit > 0
        ? (outstandingBalance / effectiveCreditLimit) * 100
        : 0;

    // Payment Performance Calculation
    const paidInvoicesWithPayments = payments
      .filter((payment) => {
        if (!payment.invoiceDate) return false;
        const paymentDate = new Date(payment.date);
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        return paymentDate >= twelveMonthsAgo;
      })
      .map((payment) => {
        const invoiceDate = new Date(payment.invoiceDate!);
        const paymentDate = new Date(payment.date);
        const daysDiff = Math.floor(
          (paymentDate.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysDiff;
      });

    const averagePaymentDays =
      paidInvoicesWithPayments.length > 0
        ? Math.round(
            paidInvoicesWithPayments.reduce((sum, days) => sum + days, 0) /
              paidInvoicesWithPayments.length
          )
        : customer.dso;

    // Last payment date
    const sortedPayments = [...payments].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const lastPaymentDate =
      sortedPayments.length > 0 ? sortedPayments[0].date : customer.lastPayment;

    return {
      outstandingBalance,
      outstandingCount,
      overdueBalance,
      overdueCount,
      effectiveCreditLimit,
      availableCredit,
      creditUsedPercentage,
      averagePaymentDays,
      lastPaymentDate,
    };
  }, [customer, metricTotals, groupMetricTotals, payments, isNonBaseCurrencyActive, isConsolidatedView]);

  // Get progress bar accent based on credit usage
  const getCreditProgressAccent = (
    percentage: number
  ): "success" | "warning" | "destructive" => {
    if (percentage >= 80) return "destructive";
    if (percentage >= 50) return "warning";
    return "success";
  };

  // Extract payment terms number
  const paymentTermsNumber = customer.paymentTerms.replace("Net ", "");

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Outstanding Balance Card */}
      {(() => {
        // effectiveCurrency already accounts for Any Currency dropdown, CurrencyMode,
        // and non-base non-any-currency customers — use it directly.
        const displayBalance = metrics.outstandingBalance;
        const displayCurrency = effectiveCurrency;
        return (
          <Card
            className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-orange-500"
            onClick={() => onCardClick("outstanding")}
            role="button"
            tabIndex={0}
            aria-label={`Outstanding Balance: ${formatCurrencyDetailed(displayBalance, displayCurrency)}. Click to view details`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick("outstanding");
              }
            }}
          >
            <CardContent className="pt-6">
              <div className="relative">
                <div className="absolute top-0 right-0">
                  <div className="p-2 rounded-full bg-orange-100 flex-shrink-0">
                    <PoundSterling className="h-4 w-4 text-orange-600" />
                  </div>
                </div>
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>
                  Outstanding Balance
                  <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} />
                </p>
                <p
                  className="mt-1 transition-opacity duration-150"
                  style={{ fontSize: '22px', fontWeight: 600, color: '#111827', opacity: isVisible ? 1 : 0 }}
                >
                  {formatCurrencyDetailed(displayBalance, displayCurrency)}
                </p>
                <p className="mt-1" style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>
                  {metrics.outstandingCount}{" "}
                  {metrics.outstandingCount === 1
                    ? "outstanding transaction"
                    : "outstanding transactions"}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Overdue Balance Card */}
      {(() => {
        // Uses same effectiveCurrency as Outstanding Balance card.
        const displayBalance = metrics.overdueBalance;
        const displayCurrency = effectiveCurrency;
        return (
          <Card
            className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-red-500"
            onClick={() => onCardClick("overdue")}
            role="button"
            tabIndex={0}
            aria-label={`Overdue Balance: ${formatCurrencyDetailed(displayBalance, displayCurrency)}. Click to view details`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick("overdue");
              }
            }}
          >
            <CardContent className="pt-6">
              <div className="relative">
                <div className="absolute top-0 right-0">
                  <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  </div>
                </div>
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>
                  Overdue Balance
                  <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} />
                </p>
                <p
                  className="mt-1 transition-opacity duration-150"
                  style={{ fontSize: '22px', fontWeight: 600, color: '#111827', opacity: isVisible ? 1 : 0 }}
                >
                  {formatCurrencyDetailed(displayBalance, displayCurrency)}
                </p>
                <p className="mt-1" style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>
                  {metrics.overdueCount === 0
                    ? "No overdue transactions"
                    : `${metrics.overdueCount} ${
                        metrics.overdueCount === 1 ? "overdue transaction" : "overdue transactions"
                      }`}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Credit Available Card */}
      {(() => {
        // Uses same effectiveCurrency as Outstanding/Overdue cards.
        // The utilisation percentage is currency-agnostic and remains unchanged.
        const displayCurrency = effectiveCurrency;
        const displayAvailable = metrics.availableCredit;
        const displayLimit = metrics.effectiveCreditLimit;
        const hasNoCreditLimit = displayLimit === 0;
        return (
          <Card
            className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-green-500"
            onClick={() => onCardClick("credit")}
            role="button"
            tabIndex={0}
            aria-label={hasNoCreditLimit ? "Credit Available: No Credit Limit Set. Click to view details" : `Credit Available: ${formatCreditLimit(displayAvailable, displayCurrency)}. Click to view details`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick("credit");
              }
            }}
          >
            <CardContent className="pt-6">
              <div className="relative">
                <div className="absolute top-0 right-0">
                  <div className="p-2 rounded-full bg-green-100 flex-shrink-0">
                    <CreditCard className="h-4 w-4 text-green-600" />
                  </div>
                </div>
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>
                  Credit Available
                  <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} alwaysGroup />
                </p>
                {hasNoCreditLimit ? (
                  <p className="mt-1" style={{ fontSize: '15px', fontWeight: 500, color: '#6b7280' }}>No Credit Limit Set</p>
                ) : (
                  <>
                    <p
                      className="mt-1 transition-opacity duration-150"
                      style={{ fontSize: '22px', fontWeight: 600, color: '#111827', opacity: isVisible ? 1 : 0 }}
                    >
                      {formatCreditLimit(displayAvailable, displayCurrency)}
                    </p>
                    <p
                      className="mt-1 mb-2 transition-opacity duration-150"
                      style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280', opacity: isVisible ? 1 : 0 }}
                    >
                      Limit: {formatCreditLimit(displayLimit, displayCurrency)}{' '}
                      <strong>({isConsolidatedView && isMultiCompanyGroup ? 'Group' : 'Company'} used {metrics.creditUsedPercentage.toFixed(1)}%)</strong>
                    </p>
                    <Progress
                      value={metrics.creditUsedPercentage}
                      className="h-1.5"
                      accent={getCreditProgressAccent(metrics.creditUsedPercentage)}
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Payment Performance Card */}
      <Card
        className="cursor-pointer transition-all hover:shadow-md border-l-4 border-l-blue-500"
        onClick={() => onCardClick("performance")}
        role="button"
        tabIndex={0}
        aria-label={`Payment Performance: ${metrics.averagePaymentDays} days average. Click to view details`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onCardClick("performance");
          }
        }}
      >
        <CardContent className="pt-6">
          <div className="relative">
            <div className="absolute top-0 right-0">
              <div className="p-2 rounded-full bg-blue-100 flex-shrink-0">
                <TrendingUp className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>
              Payment Performance
              <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} />
            </p>
            <p className="mt-1" style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }}>
              {metrics.averagePaymentDays} days
            </p>
            <p className="mt-1" style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>
              Average payment time • Terms: Net {paymentTermsNumber}
            </p>
            <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }}>
              Last payment: {formatDateShort(metrics.lastPaymentDate)}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
